/**
 * superAdminDomains — state for the Domain Management super-admin page.
 *
 * Mirrors the shape of superAdminUsers exactly so the page's structure
 * stays familiar to anyone who has worked in UserManagement.jsx:
 *
 *   - filters / setFilters / resetFilters
 *   - actionLoadingCount with startAction / endAction
 *   - isLoading + plural data containers
 *   - one thunk per backend action (incl. bulk variants)
 *
 * Backend surface (lives under /api/v1/management/domains/):
 *
 *   GET    /registries/                           → list registries (no pagination)
 *   PATCH  /registries/{id}/                       → edit primary_domain etc.
 *
 *   GET    /use-cases/                            → list use cases (no pagination)
 *   PATCH  /use-cases/{id}/                        → edit defaults / reserved list
 *   GET    /use-cases/{id}/stats/                  → headline counts for the Tab 1 card
 *
 *   GET    /practice-configs/?...                  → paginated grid rows
 *   POST   /practice-configs/                      → create one
 *   PATCH  /practice-configs/{id}/                 → update one (inline edit)
 *   DELETE /practice-configs/{id}/                 → delete one
 *   POST   /practice-configs/bulk-update/          → upsert up to 500
 *   POST   /practice-configs/bulk-verify/          → batch verify
 *   POST   /practice-configs/{id}/mark-verified/   → single-row verify
 *   GET    /practice-configs/resolved-domain/?...  → live preview (never writes)
 *   GET    /practice-configs/{id}/history/         → audit-log feed
 */
import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
import { callAdminApi } from '../../utils/apiCall';
import { invalidateResolvedDomain } from '../../hooks/useResolvedDomain';

const initialState = {
    isLoading: false,
    actionLoadingCount: 0,

    // Tab 1 — Use Cases
    useCases: [],
    useCaseStats: {}, // keyed by use_case_id

    // Tab 2 — Practice Configs (the workhorse)
    practiceConfigs: { results: [], count: 0 },
    filters: {
        practice_name: '',
        use_case_key: '',
        mode: '',
        verification_status: '',
        subdomain: '',
        needs_attention: '',
        page: 1,
        pageSize: 25,
    },
    selectedConfig: null,

    // Tab 3 — Registries
    registries: [],

    // Practice picker (Autocomplete in the New / Edit drawer)
    practices: [],

    // Side drawer: history feed for one config
    selectedHistory: { results: [], count: 0 },

    // Resolver preview (Tab 2 inline subdomain editor lives reads this)
    resolvedPreview: null, // { base_url, mode, subdomain, layer, ... } | null
};

export const slice = createSlice({
    name: 'superAdminDomains',
    initialState,
    reducers: {
        setIsLoading(state, action) {
            state.isLoading = action.payload;
        },
        startAction(state) {
            state.actionLoadingCount += 1;
        },
        endAction(state) {
            state.actionLoadingCount = Math.max(0, state.actionLoadingCount - 1);
        },

        // Use Cases
        setUseCases(state, action) {
            state.useCases = action.payload;
        },
        setUseCaseStats(state, action) {
            const { useCaseId, stats } = action.payload;
            state.useCaseStats[useCaseId] = stats;
        },

        // Practice Configs
        setPracticeConfigs(state, action) {
            state.practiceConfigs = action.payload;
        },
        setSelectedConfig(state, action) {
            state.selectedConfig = action.payload;
        },
        setFilters(state, action) {
            state.filters = { ...state.filters, ...action.payload };
        },
        resetFilters(state) {
            state.filters = initialState.filters;
        },

        // Registries
        setRegistries(state, action) {
            state.registries = action.payload;
        },

        // Practice picker
        setPractices(state, action) {
            state.practices = action.payload;
        },

        // History
        setSelectedHistory(state, action) {
            state.selectedHistory = action.payload;
        },
        clearSelectedHistory(state) {
            state.selectedHistory = { results: [], count: 0 };
        },

        // Resolver preview
        setResolvedPreview(state, action) {
            state.resolvedPreview = action.payload;
        },
        clearResolvedPreview(state) {
            state.resolvedPreview = null;
        },
    },
});

export const {
    setIsLoading,
    startAction,
    endAction,
    setUseCases,
    setUseCaseStats,
    setPracticeConfigs,
    setSelectedConfig,
    setFilters,
    resetFilters,
    setRegistries,
    setPractices,
    setSelectedHistory,
    clearSelectedHistory,
    setResolvedPreview,
    clearResolvedPreview,
} = slice.actions;

export default slice.reducer;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const _surfaceError = (response, fallback) => {
    dispatch(
        updateError({
            success: false,
            message: response?.data?.detail || fallback,
        })
    );
};

// ---------------------------------------------------------------------------
// Use Cases
// ---------------------------------------------------------------------------

export function fetchUseCases() {
    return async () => {
        dispatch(slice.actions.setIsLoading(true));
        try {
            const response = await callAdminApi('/api/v1/management/domains/use-cases/');
            if (response.ok) {
                // Endpoint returns an array (pagination_class = None on this viewset).
                dispatch(slice.actions.setUseCases(response.data || []));
            } else {
                _surfaceError(response, 'Failed to fetch domain use cases');
                dispatch(slice.actions.setUseCases([]));
            }
        } catch (e) {
            _surfaceError(null, 'Failed to fetch domain use cases');
            dispatch(slice.actions.setUseCases([]));
        }
        dispatch(slice.actions.setIsLoading(false));
    };
}

export function patchUseCase(id, payload, onSuccess) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(
                `/api/v1/management/domains/use-cases/${id}/`,
                'PATCH',
                payload
            );
            if (response.ok) {
                // Optimistically refresh the list so cards rerender immediately.
                dispatch(fetchUseCases());
                if (onSuccess) onSuccess(response.data);
            } else {
                _surfaceError(response, 'Failed to update use case');
            }
        } catch (e) {
            _surfaceError(null, 'Failed to update use case');
        }
        dispatch(slice.actions.endAction());
    };
}

export function fetchUseCaseStats(useCaseId) {
    return async () => {
        try {
            const response = await callAdminApi(
                `/api/v1/management/domains/use-cases/${useCaseId}/stats/`
            );
            if (response.ok) {
                dispatch(slice.actions.setUseCaseStats({ useCaseId, stats: response.data }));
            }
        } catch (e) {
            // Stats failures are silent — the card just shows "—".
        }
    };
}

// ---------------------------------------------------------------------------
// Registries
// ---------------------------------------------------------------------------

export function fetchRegistries() {
    return async () => {
        try {
            const response = await callAdminApi('/api/v1/management/domains/registries/');
            if (response.ok) {
                dispatch(slice.actions.setRegistries(response.data || []));
            } else {
                _surfaceError(response, 'Failed to fetch registries');
            }
        } catch (e) {
            _surfaceError(null, 'Failed to fetch registries');
        }
    };
}

// Practice picker — used by the New / Edit drawer's Autocomplete.
// Reuses the existing super-admin practices list endpoint
// (SuperAdminPracticeListViewSet, no pagination, returns [{id, name, ...}]).
export function fetchPracticesForPicker() {
    return async () => {
        try {
            const response = await callAdminApi('/api/v1/management/practices/');
            if (response.ok) {
                dispatch(slice.actions.setPractices(response.data || []));
            }
        } catch (e) {
            // Picker failure is non-blocking — the operator can still
            // see existing rows; we just can't help them add new ones
            // by name. Silent fail rather than spam the user.
        }
    };
}

export function patchRegistry(id, payload, onSuccess) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(
                `/api/v1/management/domains/registries/${id}/`,
                'PATCH',
                payload
            );
            if (response.ok) {
                dispatch(fetchRegistries());
                if (onSuccess) onSuccess(response.data);
            } else {
                _surfaceError(response, 'Failed to update registry');
            }
        } catch (e) {
            _surfaceError(null, 'Failed to update registry');
        }
        dispatch(slice.actions.endAction());
    };
}

// ---------------------------------------------------------------------------
// Practice Configs
// ---------------------------------------------------------------------------

export function fetchPracticeConfigs(filters) {
    return async () => {
        dispatch(slice.actions.setIsLoading(true));
        try {
            const params = new URLSearchParams();
            if (filters?.practice_name) params.append('practice_name', filters.practice_name);
            if (filters?.use_case_key) params.append('use_case_key', filters.use_case_key);
            if (filters?.mode) params.append('mode', filters.mode);
            if (filters?.verification_status)
                params.append('verification_status', filters.verification_status);
            if (filters?.subdomain) params.append('subdomain', filters.subdomain);
            if (filters?.needs_attention) params.append('needs_attention', 'true');
            if (filters?.page) params.append('offset', (filters.page - 1) * (filters.pageSize || 25));
            if (filters?.pageSize) params.append('limit', filters.pageSize);
            const q = params.toString();
            const response = await callAdminApi(
                `/api/v1/management/domains/practice-configs/${q ? `?${q}` : ''}`
            );
            if (response.ok) {
                dispatch(slice.actions.setPracticeConfigs(response.data));
            } else {
                _surfaceError(response, 'Failed to fetch practice configs');
                dispatch(slice.actions.setPracticeConfigs({ results: [], count: 0 }));
            }
        } catch (e) {
            _surfaceError(null, 'Failed to fetch practice configs');
            dispatch(slice.actions.setPracticeConfigs({ results: [], count: 0 }));
        }
        dispatch(slice.actions.setIsLoading(false));
    };
}

export function createPracticeConfig(payload, onSuccess, onError) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(
                '/api/v1/management/domains/practice-configs/',
                'POST',
                payload
            );
            if (response.ok) {
                // Drop the cached consent_form_config for this practice
                // so the next send-flow hits fresh data. Without this,
                // a consent-send fired immediately after the super
                // admin saves would still show the old URL.
                invalidateResolvedDomain(payload?.practice);
                if (onSuccess) onSuccess(response.data);
            } else {
                _surfaceError(response, 'Failed to create practice config');
                if (onError) onError(response.data);
            }
        } catch (e) {
            _surfaceError(null, 'Failed to create practice config');
            if (onError) onError(e);
        }
        dispatch(slice.actions.endAction());
    };
}

export function updatePracticeConfig(id, payload, onSuccess, onError) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(
                `/api/v1/management/domains/practice-configs/${id}/`,
                'PATCH',
                payload
            );
            if (response.ok) {
                // Server returns the updated row including practice id —
                // use that, not the request payload, since PATCH may not
                // have included ``practice`` (it's immutable).
                invalidateResolvedDomain(response.data?.practice);
                if (onSuccess) onSuccess(response.data);
            } else {
                _surfaceError(response, 'Failed to update practice config');
                if (onError) onError(response.data);
            }
        } catch (e) {
            _surfaceError(null, 'Failed to update practice config');
            if (onError) onError(e);
        }
        dispatch(slice.actions.endAction());
    };
}

export function deletePracticeConfig(id, onSuccess) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            // Read the row FIRST so we know which practice cache to drop.
            // The DELETE response body is typically empty.
            const lookup = await callAdminApi(
                `/api/v1/management/domains/practice-configs/${id}/`
            );
            const practiceId = lookup?.ok ? lookup.data?.practice : null;

            const response = await callAdminApi(
                `/api/v1/management/domains/practice-configs/${id}/`,
                'DELETE'
            );
            if (response.ok) {
                if (practiceId) invalidateResolvedDomain(practiceId);
                if (onSuccess) onSuccess();
            } else {
                _surfaceError(response, 'Failed to delete practice config');
            }
        } catch (e) {
            _surfaceError(null, 'Failed to delete practice config');
        }
        dispatch(slice.actions.endAction());
    };
}

export function bulkUpdateConfigs(rows, onComplete) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(
                '/api/v1/management/domains/practice-configs/bulk-update/',
                'POST',
                { rows }
            );
            if (response.ok) {
                // Drop every touched practice from the resolver cache —
                // bulk-update can fan across many practices, so the
                // request payload is the authoritative source of which
                // ones to invalidate.
                (rows || []).forEach((row) => {
                    if (row?.practice) invalidateResolvedDomain(row.practice);
                });
                if (onComplete) onComplete(response.data);
                // Surface partial failures to the toast so operators notice.
                if (response.data.error_count > 0) {
                    _surfaceError(
                        { data: { detail: `${response.data.error_count} row(s) failed` } },
                        ''
                    );
                }
            } else {
                _surfaceError(response, 'Bulk update failed');
            }
        } catch (e) {
            _surfaceError(null, 'Bulk update failed');
        }
        dispatch(slice.actions.endAction());
    };
}

export function bulkVerifyConfigs(ids, status, onComplete) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(
                '/api/v1/management/domains/practice-configs/bulk-verify/',
                'POST',
                { ids, status }
            );
            if (response.ok) {
                if (onComplete) onComplete(response.data);
            } else {
                _surfaceError(response, 'Bulk verify failed');
            }
        } catch (e) {
            _surfaceError(null, 'Bulk verify failed');
        }
        dispatch(slice.actions.endAction());
    };
}

export function markConfigVerified(id, onSuccess) {
    return async () => {
        dispatch(slice.actions.startAction());
        try {
            const response = await callAdminApi(
                `/api/v1/management/domains/practice-configs/${id}/mark-verified/`,
                'POST',
                {}
            );
            if (response.ok) {
                if (onSuccess) onSuccess(response.data);
            } else {
                _surfaceError(response, 'Failed to mark verified');
            }
        } catch (e) {
            _surfaceError(null, 'Failed to mark verified');
        }
        dispatch(slice.actions.endAction());
    };
}

export function fetchResolvedPreview(practiceId, useCaseKey) {
    return async () => {
        if (!practiceId || !useCaseKey) {
            dispatch(slice.actions.clearResolvedPreview());
            return;
        }
        try {
            const params = new URLSearchParams({
                practice: String(practiceId),
                use_case: useCaseKey,
            });
            const response = await callAdminApi(
                `/api/v1/management/domains/practice-configs/resolved-domain/?${params.toString()}`
            );
            if (response.ok) {
                dispatch(slice.actions.setResolvedPreview(response.data));
            } else {
                dispatch(slice.actions.clearResolvedPreview());
            }
        } catch (e) {
            dispatch(slice.actions.clearResolvedPreview());
        }
    };
}

export function fetchConfigHistory(configId) {
    return async () => {
        try {
            const response = await callAdminApi(
                `/api/v1/management/domains/practice-configs/${configId}/history/`
            );
            if (response.ok) {
                // The endpoint paginates when there are many rows; tolerate both shapes.
                const data = response.data;
                const normalised = Array.isArray(data)
                    ? { results: data, count: data.length }
                    : data;
                dispatch(slice.actions.setSelectedHistory(normalised));
            } else {
                _surfaceError(response, 'Failed to fetch history');
            }
        } catch (e) {
            _surfaceError(null, 'Failed to fetch history');
        }
    };
}
